import React from "react";
import { Box, Flex, Spinner } from "theme-ui";
import moment from "moment-timezone";

import H5 from "components/typography/H5";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { mnt, mntCard } from "theme/maintenanceTokens";

/**
 * MASTER_PLAN.md §3.9 #22 — plan activity feed (mockup 13).
 * Renders whatever the /activity endpoint returned. Session 60 redesign
 * (Jack): the panel is now full-width at the bottom of the plan page, so
 * the old single narrow column of floating time+label rows left most of
 * the width empty and read as low-information. This version:
 *   - flows events into a responsive 2-column grid at width (auto-fit),
 *     so the feed is a compact block instead of one thin ribbon;
 *   - gives each event a type chip (Received / RFQ / Location / Batch /
 *     Service / Invoice) so a thin label ("Location added") still carries
 *     meaning at a glance;
 *   - puts the relative time in a fixed left column on the same row as the
 *     label, tightening each event to a single dense line;
 *   - keeps a colored type dot, and caps height with internal scroll so
 *     the card never runs the page.
 * Dot + chip color communicate event type:
 *   green service_event · blue rfq/invoice · amber wo_received ·
 *   grey site_added/group_created.
 */
export type ActivityEvent = {
  type:
    | "wo_received"
    | "rfq_created"
    | "site_added"
    | "group_created"
    | "service_event"
    | "invoice_created";
  refId: string;
  label: string;
  at: string;
};

export default function RecentActivityPanel({
  events,
  loading,
}: {
  events: ActivityEvent[];
  loading?: boolean;
}) {
  return (
    <Box>
      <Flex
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
        }}
      >
        <H5
          fontFamily="Poppins"
          fontWeight={600}
          fontSize={"14px"}
          margin={0}
        >
          Recent activity
        </H5>
        {!loading && events.length > 0 && (
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            margin={0}
            color={mnt.textFaint}
          >
            {events.length.toLocaleString()} event
            {events.length === 1 ? "" : "s"}
          </Subtext>
        )}
      </Flex>
      <Box
        sx={{
          ...mntCard,
          padding: "14px 16px",
          minHeight: "80px",
          // Full-width at the bottom of the plan page. Cap the height and
          // scroll internally so a long feed never runs the page; ~340px
          // shows a good block of the 2-column grid.
          maxHeight: "340px",
          overflowY: "auto",
        }}
      >
        {loading ? (
          <Box sx={{ textAlign: "center", paddingY: "8px" }}>
            <Spinner size={18} />
          </Box>
        ) : events.length === 0 ? (
          <Subtext
            fontSize={"12px"}
            fontFamily={"Poppins"}
            margin={0}
            color={mnt.textMuted}
          >
            No activity yet on this plan.
          </Subtext>
        ) : (
          <Box
            sx={{
              display: "grid",
              // Two columns at width, one when narrow. Fills the full-width
              // card instead of leaving the right half empty.
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              columnGap: "28px",
              rowGap: "2px",
            }}
          >
            {events.map((e, idx) => {
              const meta = typeMeta(e.type);
              return (
                <Flex
                  key={`${e.type}-${e.refId}-${idx}`}
                  sx={{
                    alignItems: "center",
                    gap: "10px",
                    padding: "7px 0",
                    borderBottom: `1px solid ${mnt.bgSubtle}`,
                    minWidth: 0,
                  }}
                >
                  {/* Relative time — fixed-width left column so labels line
                      up cleanly down the feed. */}
                  <Subtext
                    fontSize={"11px"}
                    fontFamily={"Poppins"}
                    margin={0}
                    color={mnt.textFaint}
                    sx={{
                      flex: "0 0 66px",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {relTime(e.at)}
                  </Subtext>

                  {/* Type dot on a short rail segment. */}
                  <Box
                    sx={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "4px",
                      background: meta.color,
                      flexShrink: 0,
                    }}
                  />

                  {/* Type chip — gives a thin label meaning at a glance. */}
                  <Box
                    sx={{
                      flex: "0 0 auto",
                      fontFamily: "Poppins",
                      fontSize: "10px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      color: meta.color,
                      background: meta.pill,
                      borderRadius: "10px",
                      padding: "1px 8px",
                    }}
                  >
                    {meta.chip}
                  </Box>

                  {/* Event label. */}
                  <Span
                    fontFamily={"Poppins"}
                    fontSize={"12px"}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {e.label}
                  </Span>
                </Flex>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/** Type -> color + pill bg + short chip label. Colors match the mnt
 * status palette so the feed reads consistently with the rest of the
 * plan page. */
function typeMeta(t: ActivityEvent["type"]): {
  color: string;
  pill: string;
  chip: string;
} {
  switch (t) {
    case "service_event":
      return { color: mnt.green, pill: mnt.greenPill, chip: "Service" };
    case "rfq_created":
      return { color: mnt.link, pill: mnt.corrigoPill, chip: "RFQ" };
    case "invoice_created":
      return { color: mnt.link, pill: mnt.corrigoPill, chip: "Invoice" };
    case "wo_received":
      return { color: mnt.amber, pill: mnt.amberPill, chip: "Received" };
    case "group_created":
      return { color: mnt.grey, pill: mnt.greyPill, chip: "Batch" };
    case "site_added":
    default:
      return { color: mnt.grey, pill: mnt.greyPill, chip: "Location" };
  }
}

function relTime(at: string): string {
  if (!at) return "";
  const m = moment(at);
  if (!m.isValid()) return "";
  const now = moment();
  const diffH = now.diff(m, "hours");
  if (diffH < 12) return m.format("h:mm A");
  if (now.isSame(m, "day")) return "Today";
  if (now.clone().subtract(1, "day").isSame(m, "day")) return "Yesterday";
  const diffD = now.diff(m, "days");
  if (diffD < 7) return `${diffD}d ago`;
  return m.format("MMM D");
}
